/**
 * Door Tool - Hosted Door Component Placement
 * 
 * Doors are hosted elements that cut their host walls.
 * Features:
 * - Wall hosting
 * - Swing direction
 * - Tag on placement
 * - Multiple door family types
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from './BaseTool';
import { DoorElement, DoorGeometryData, WallElement } from '../core/types';
import { TagElement } from './TagTool';
import { familyManager } from '../core/FamilyManager';

export class DoorTool extends BaseTool {
    id = 'door';
    name = 'Door';
    cursor = 'copy';

    private currentWall: WallElement | null = null;
    private currentPosition: number = 0; // 0-1

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.hostManager) return;
        if (event.button !== 0) return;

        // Resolve the host at click time (move events may not have fired over
        // the wall yet, e.g. right after tool activation).
        const host = this.hostManager.findHost(event.intersections, { categories: ['Walls'] })
            ?? this.hostManager.findHostByRay(this.context.scene, event.ray, { categories: ['Walls'] });

        if (host && host.isValid) {
            if (!this.currentWall || this.currentWall.id !== host.elementId) {
                const wall = this.context.bimStore.project.elements.get(host.elementId) as WallElement;
                if (!wall || !wall.geometryData?.start) return;
                this.currentWall = wall;
                this.currentPosition = this.projectOnWall(wall, host.point);
            }
            this.createDoorElement();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.hostManager) return;

        // Use HostManager to find a Wall. Event intersections only cover
        // meshes registered with R3F handlers, so fall back to a scene
        // raycast for BIM walls.
        const host = this.hostManager.findHost(event.intersections, { categories: ['Walls'] })
            ?? this.hostManager.findHostByRay(this.context.scene, event.ray, { categories: ['Walls'] });

        if (host && host.isValid) {
            const wall = this.context.bimStore.project.elements.get(host.elementId) as WallElement;
            if (!wall || !wall.geometryData?.start) {
                this.currentWall = null;
                this.context.appStore.setToolPreview(null);
                return;
            }

            this.currentWall = wall;
            this.currentPosition = this.projectOnWall(wall, host.point);

            this.updatePreview();
        } else {
            this.currentWall = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    private projectOnWall(wall: WallElement, point: THREE.Vector3): number {
        const wallStart = new THREE.Vector3(...wall.geometryData.start);
        const wallEnd = new THREE.Vector3(...wall.geometryData.end);
        const wallLine = new THREE.Line3(wallStart, wallEnd);

        const closestPoint = new THREE.Vector3();
        wallLine.closestPointToPoint(point, true, closestPoint);

        const length = wallStart.distanceTo(wallEnd);
        if (length < 1e-6) return 0;
        const dist = wallStart.distanceTo(closestPoint);
        return Math.max(0, Math.min(1, dist / length));
    }

    onPointerUp(): void { }

    private flipHand: boolean = false;
    private flipFacing: boolean = false;

    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Space') {
            // Cycle: Normal -> Flip Hand -> Flip Facing -> Both -> Normal
            if (!this.flipHand && !this.flipFacing) {
                this.flipHand = true;
            } else if (this.flipHand && !this.flipFacing) {
                this.flipHand = false;
                this.flipFacing = true;
            } else if (!this.flipHand && this.flipFacing) {
                this.flipHand = true;
            } else {
                this.flipHand = false;
                this.flipFacing = false;
            }
            this.updatePreview();
        }
    }

    private updatePreview() {
        if (!this.context || !this.currentWall) return;

        this.context.appStore.setToolPreview({
            type: 'door',
            wallId: this.currentWall.id,
            position: this.currentPosition,
            width: 0.915,
            flipHand: this.flipHand,
            flipFacing: this.flipFacing
        });
    }

    private createDoorElement() {
        if (!this.context || !this.currentWall) return;

        // ... (Existing setup)
        const doorType = 'Single-Flush: 0915 x 2134mm';
        const [familyName, typeName] = doorType.split(': ').map(s => s.trim());
        const typeSymbol = familyManager.getType(familyName, typeName);

        let widthM = 0.915;
        let heightM = 2.134;

        if (typeSymbol) {
            if (typeSymbol.parameters['Width']) widthM = typeSymbol.parameters['Width'] as number;
            if (typeSymbol.parameters['Height']) heightM = typeSymbol.parameters['Height'] as number;
        }

        const sillHeightM = 0;

        const geometryData: DoorGeometryData = {
            hostWallId: this.currentWall.id,
            position: this.currentPosition,
            width: widthM,
            height: heightM,
            sillHeight: sillHeightM
        };

        const door: DoorElement = {
            id: uuidv4(),
            category: 'Doors',
            type: doorType,
            parameters: {
                'Width': `${widthM.toFixed(2)} m`,
                'Height': `${heightM.toFixed(2)} m`,
                'Sill Height': `${sillHeightM} mm`,
                'Host': this.currentWall.id,
                'Level': this.currentWall.parameters['Base Constraint'],
                'Flip Facing': this.flipFacing,
                'Flip Hand': this.flipHand,
                'Fire Rating': typeSymbol?.parameters['Fire Rating'] || '',
                'Material': typeSymbol?.parameters['Material'] || 'Default'
            },
            constraints: [
                {
                    type: 'HOST_CONTAINED',
                    targetId: this.currentWall.id,
                    data: { positionAlongHost: this.currentPosition }
                }
            ],
            geometryData
        };

        this.context.bimStore.addElement(door);

        // Honor "Tag on Placement" option: attach a door tag on the wall at
        // the door's position along its length.
        if (this.context.bimStore.toolProperties.tagOnPlacement && this.currentWall.geometryData?.start && this.currentWall.geometryData?.end) {
            const wallStart = new THREE.Vector3(...this.currentWall.geometryData.start);
            const wallEnd = new THREE.Vector3(...this.currentWall.geometryData.end);
            const tagPos = wallStart.clone().lerp(wallEnd, this.currentPosition);
            tagPos.z += 1.6; // head height above floor

            const tag: TagElement = {
                id: uuidv4(),
                category: 'Tags',
                type: 'Tag: Doors',
                parameters: {
                    'Target Category': 'Doors',
                    'Target Id': door.id,
                    'Value': door.type,
                    'Parameter': 'Type Mark'
                },
                constraints: [
                    { type: 'HOST_CONTAINED', targetId: door.id, data: {} }
                ],
                geometryData: {
                    position: [tagPos.x, tagPos.y, tagPos.z],
                    targetId: door.id
                }
            };
            this.context.bimStore.addElement(tag);
        }
    }
}
